import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Orbit } from "lucide-react";
import { Link } from "react-router-dom";
import MagneticButton from "../components/UI/MagneticButton";

const orbitTransition = {
  duration: 18,
  repeat: Infinity,
  ease: "linear",
};

const NotFoundPage = () => (
  <section className="relative isolate flex min-h-screen items-center overflow-hidden bg-[#070807] px-5 pb-20 pt-28 text-white sm:px-8">
    <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <div className="absolute left-1/2 top-1/2 h-[34rem] w-[34rem] -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#d9f99d]/10" />
      <motion.div
        animate={{ rotate: 360 }}
        transition={orbitTransition}
        className="absolute left-1/2 top-1/2 h-[44rem] w-[44rem] -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-[#d9f99d]/20"
      >
        <span className="absolute left-1/2 top-0 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#d9f99d] shadow-[0_0_24px_8px_rgba(217,249,157,0.35)]" />
      </motion.div>
      <div className="absolute left-1/2 top-1/2 h-[28rem] w-[28rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#d9f99d]/[0.09] blur-[100px]" />
      <div
        className="absolute inset-0 opacity-[0.18]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
          backgroundSize: "52px 52px",
          maskImage: "radial-gradient(circle at center, black, transparent 70%)",
        }}
      />
    </div>

    <div className="mx-auto flex w-full max-w-4xl flex-col items-center text-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.82 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="relative"
      >
        <div className="absolute left-1/2 top-1/2 -z-10 h-40 w-40 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#d9f99d]/25" />
        <Orbit className="text-[#d9f99d]" size={40} strokeWidth={1.25} />
      </motion.div>

      <motion.p
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.65, delay: 0.12 }}
        className="mt-9 font-mono text-[10px] font-bold uppercase tracking-[0.28em] text-[#d9f99d]"
      >
        Signal lost / Error 404
      </motion.p>

      <motion.h1
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className="mt-5 font-display text-[clamp(5.5rem,23vw,13rem)] font-extrabold leading-[0.72] tracking-[-0.11em] text-white"
      >
        404
      </motion.h1>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.65, delay: 0.31 }}
        className="mt-10 font-display text-3xl font-bold uppercase tracking-tight sm:text-5xl"
      >
        This path is still <span className="text-[#d9f99d]">uncharted.</span>
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.65, delay: 0.39 }}
        className="mt-5 max-w-md text-base leading-relaxed text-zinc-400"
      >
        The page you&apos;re looking for has moved, vanished, or never made it into our universe.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.65, delay: 0.47 }}
        className="mt-9"
      >
        <Link to="/" aria-label="Go back to the Dylora home page">
          <MagneticButton className="group flex items-center gap-3 rounded-full bg-[#d9f99d] px-7 py-3.5 text-sm font-bold text-black transition-colors hover:bg-white">
            <span>Back to home</span>
            <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </MagneticButton>
        </Link>
      </motion.div>
    </div>
  </section>
);

export default NotFoundPage;
